import React from "react";
import { Box, styled, Typography, useTheme } from "@mui/material";
import { useThemeMode } from "@/colors";
import { useColors } from "@/hooks";

// Styled components for the banner
const BannerContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  overflow: "hidden",
  backgroundColor: theme.palette.background.default,
  padding: "20px 0",
  // borderTop: "1px solid #e9ecef",
  // borderBottom: "1px solid #e9ecef",
  position: "relative",
  marginTop: "96px",
  marginBottom: "128px",

  [theme.breakpoints.up("md")]: {
    padding: "30px 0",
  },
}));

const ScrollingWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  animation: "scroll-mobile 30s linear infinite",
  [theme.breakpoints.up("md")]: {
    animation: "scroll 30s linear infinite",
  },
  gap: "60px",

  "&:hover": {
    animationPlayState: "paused",
  },

  "@keyframes scroll": {
    "0%": {
      transform: "translateX(0%)",
    },
    "100%": {
      transform: "translateX(-2620px)",
    },
  },
  "@keyframes scroll-mobile": {
      "0%": {
        transform: "translateX(0%)",
      },
      "100%": {
        transform: "translateX(-2350px)",
      },
    },
}));

const LogoItem = styled("a")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "160px",
  height: "60px",
  padding: "10px 20px",
  background: theme.palette.mode === "light" ? theme.palette.background.paper : `linear-gradient(0deg, ${useColors().background.brand.lightest}, ${useColors().background.brand.lighter})`,
  borderRadius: "8px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
  textDecoration: "none",
  transition: "all 0.3s ease",
  border: `1px solid ${theme.palette.divider}`,
  filter: "grayscale(100%)",
  maxWidth: "160px",

  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.12)",
    borderColor: theme.palette.primary.light,
    filter: "grayscale(0%)",
  },

  "&:focus": {
    outline: `2px solid ${theme.palette.primary.light}`,
    outlineOffset: "2px",
  },

  [theme.breakpoints.up("md")]: {
    minWidth: "180px",
    width: "180px",
    height: "70px",
    padding: "15px 25px",
  },
}));

const LogoImage = styled("img")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    maxWidth: "150px",
    maxHeight: "70px",
  },
  maxWidth: "120px",
  maxHeight: "60px",
  // objectFit: "contain",
}));

const LogoText = styled("span")(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 600,
  color: "#6c757d",
  textAlign: "center",

  [theme.breakpoints.up("md")]: {
    fontSize: "16px",
  },
}));

// Sample logo data - replace with your actual logos and links
const logoData = [
  {
    id: 1,
    name: "MappingGIS",
    url: "https://mappinggis.com/2017/04/opciones-para-crear-mapas-personalizados/#:~:text=mappin,-Mappin",
    logo: "/mappingGIS_logo.png",
  },
  {
    id: 2,
    name: "Gribnica",
    url: "https://gribnica.online/instruments?tag=2214",
    logo: "/gribnica_logo.svg",
  },
  {
    id: 3,
    name: "Maestro Alberto",
    url: "https://www.maestroalberto.it/tag/download/#:~:text=Mappin",
    logo: "/maestro_logo.png",
  },
  {
    id: 4,
    name: "Neoxion",
    url: "https://neoxion.net/maps-customized/",
    logo: "/neoxion_logo.png",
  },
  {
    id: 5,
    name: "LBC",
    url: "https://iesluisbuenocrespo.es/herramientasdigitales/",
    logo: "lbc_logo.png",
  },
  {
    id: 6,
    name: "Genbeta",
    url: "https://www.genbeta.com/web/esta-web-puedes-crear-tus-propios-mapas-alta-definicion-listos-para-imprimir",
    logo: "/genbeta_logo.png",
  },
  {
    id: 7,
    name: "Tech.lat",
    url: "https://tech.lat/post/E44823C1617D4967094316EF1B5F46F1.html",
    logo: "/techlat_logo.png",
  },
  {
    id: 8,
    name: "Editing Corp",
    url: "https://www.editingcorp.com/websites-every-graphic-designer-should-bookmark/",
    logo: "editingcorp_logo.png",
  },
  {
    id: 9,
    name: "WWWHats New",
    url: "https://wwwhatsnew.com/2022/02/24/como-crear-mapas-personalizados-gratis-en-alta-definicion/",
    logo: "/wwwhatsnew_logo.png",
  },
];

export const LogoCarousel: React.FC = () => {
  const theme = useTheme();
  const { colors } = useThemeMode();
  // Duplicate the logos to create seamless infinite scroll
  const duplicatedLogos = [...logoData, ...logoData];

  return (
    <BannerContainer>
      <Typography variant="h2" textAlign="center" pb="64px">
        As Featured On
      </Typography>
      <ScrollingWrapper maxWidth="2500px">
        {duplicatedLogos.map((logo, index) => (
          <LogoItem
            key={`${logo.id}-${index}`}
            href={logo.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Visit ${logo.name} website`}
          >
            <LogoImage
              src={logo.logo}
              alt={`${logo.name} logo`}
              loading="lazy"
            />
          </LogoItem>
        ))}
      </ScrollingWrapper>
    </BannerContainer>
  );
};
